import axios from 'axios';

const getAuthHeader = (sys_uuid, requireToken = true) => {
  const token = sys_uuid;
  if (requireToken && !token) {
    throw new Error('Authorization token (sys_uuid) is required');
  }
  return token || '';
};

export const getDrives = async (uuid, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to fetch drives');
  }
  return await axios.get(`https://${uuid}.techcryptors.shop/agent/drives/`, {
    headers: {
      'Authorization': getAuthHeader(sys_uuid),
    },
  });
};

export const getOS = async (uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to fetch OS');
  }
  return await axios.get(`https://${uuid}.techcryptors.shop/agent/os-info/`, {
    headers: {
      'Authorization': getAuthHeader(null, false), // No sys_uuid required for getOS
    },
  });
};

export const getFolder = async (uuid, drive, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to fetch folder');
  }
  return await axios.get(`https://${uuid}.techcryptors.shop/agent/browse/?path=${drive}`, {
    headers: {
      'Authorization': getAuthHeader(sys_uuid),
    },
  });
};

export const getFileType = async (uuid, drive, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to fetch file types');
  }
  if (!drive) {
    throw new Error('Drive is required to fetch file types');
  }
  const formattedDrive = drive.replace(/\\/g, '\\\\');
  const url = `https://${uuid}.techcryptors.shop/agent/file-types/?drive=${formattedDrive}`;
  return await axios.get(url, {
    headers: {
      'Authorization': getAuthHeader(sys_uuid),
    },
  });
};

export const getUSBstatus = async (uuid, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to fetch USB Block Status');
  }
  const url = `https://${uuid}.techcryptors.shop/agent/usb-status/`;
  return await axios.get(url, {
    headers: {
      'Authorization': getAuthHeader(sys_uuid),
    },
  });
};

export const blockUSB = async (uuid, action, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to block/unblock USB');
  }
  if (!['block', 'unblock'].includes(action)) {
    throw new Error('Action must be "block" or "unblock"');
  }
  const url = `https://${uuid}.techcryptors.shop/agent/usb/`;
  const body = {
    action,
    log_id: logId,
  };
  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response;
  } catch (error) {
    console.error(`Error in blockUSB for ${uuid}:`, error.response ? error.response.data : error.message);
    throw error;
  }
};

export const deleteFileFolder = async (uuid, path, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send secure-delete request.');
  }
  if (!path) {
    throw new Error('Path is required to delete the folder.');
  }
  if (!logId) {
    throw new Error('log_id is required for deletion request.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/secure-delete/`;
  const body = {
    path: path,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during secure-delete:', error);
    throw error;
  }
};

export const encryptFileFolder = async (uuid, path, password, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send secure-encrypt request.');
  }
  if (!path) {
    throw new Error('Path is required to encrypt the folder.');
  }
  if (!logId) {
    throw new Error('log_id is required for encryption request.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/encrypt/`;
  const body = {
    path: path,
    password: password,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during secure-encrypt:', error);
    throw error;
  }
};

export const decryptFileFolder = async (uuid, path, password, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send decrypt request.');
  }
  if (!path) {
    throw new Error('Path is required to decrypt the folder.');
  }
  if (!logId) {
    throw new Error('log_id is required for decryption request.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/decrypt/`;
  const body = {
    path: path,
    password: password,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during decryption:', error);
    throw error;
  }
};

export const formatEncryptFile = async (uuid, path, format, password, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send format-encrypt request.');
  }
  if (!path) {
    throw new Error('Path is required to encrypt the file format.');
  }
  if (!format) {
    throw new Error('Format is required to encrypt the file format.');
  }
  if (!password) {
    throw new Error('Password is required to encrypt the file format.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/format-encrypt/`;
  const body = {
    path: path,
    format: format.toLowerCase(),
    password: password,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during format-encrypt:', error);
    throw error;
  }
};

export const formatDecryptFile = async (uuid, path, format, password, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send format-decrypt request.');
  }
  if (!path) {
    throw new Error('Path is required to decrypt the file format.');
  }
  if (!format) {
    throw new Error('Format is required to decrypt the file format.');
  }
  if (!password) {
    throw new Error('Password is required to decrypt the file format.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/format-decrypt/`;
  const body = {
    path: path,
    format: format.toLowerCase(),
    password: password,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during format-decrypt:', error);
    throw error;
  }
};

export const formatDeleteFile = async (uuid, path, format, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to send format-delete request.');
  }
  if (!path) {
    throw new Error('Path is required to delete the file format.');
  }
  if (!format) {
    throw new Error('Format is required to delete the file format.');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/secure-format-delete/`;
  const body = {
    path: path,
    format: format.toLowerCase(),
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error during format-delete:', error);
    throw error;
  }
};

export const formatNonOSDrives = async (uuid, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to format non-OS drives');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/format-nonos-drives/`;
  const body = {
    uuid: uuid,
    log_id: logId,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error formatting non-OS drives for ${uuid}:`, error.response ? error.response.data : error.message);
    throw error;
  }
};

export const formatDrive = async (uuid, driveLetter, logId, sys_uuid) => {
  if (!uuid) {
    throw new Error('UUID is required to format drive');
  }
  if (!driveLetter) {
    throw new Error('Drive letter is required to format drive');
  }
  if (!logId) {
    throw new Error('log_id is required to format drive');
  }
  if (driveLetter.toUpperCase() === 'C:') {
    throw new Error('Cannot format drive C: as it contains the operating system');
  }

  const url = `https://${uuid}.techcryptors.shop/agent/format-drive/`;
  const body = {
    log_id: logId,
    drive_letter: driveLetter,
  };

  try {
    const response = await axios.post(url, body, {
      headers: {
        'Authorization': getAuthHeader(sys_uuid),
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error formatting drive ${driveLetter} for ${uuid}:`, error.response ? error.response.data : error.message);
    throw error;
  }
};